import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { fetchFromDentalAPI } from './common';
import './SalesTouchpoints.css';

// Score-breakdown schema is type-aware because the Circleback (Demo) evaluator
// uses a different 6-axis rubric (Rapport / Discovery / Product Presentation /
// Feature Coverage / Objection Handling / Closing). Those scores are stored in
// the same Attio columns as Call scores for historical reasons (e.g. feature
// coverage lands in `call_control_score`), but we render them with correct
// labels in the expanded row view.
const SCORE_FIELDS_BY_TYPE = {
  Call: [
    { key: 'openingScore', label: 'Opening' },
    { key: 'discoveryScore', label: 'Discovery' },
    { key: 'valuePropScore', label: 'Value Prop' },
    { key: 'objectionHandlingScore', label: 'Objection Handling' },
    { key: 'callControlScore', label: 'Call Control' },
    { key: 'closingScore', label: 'Closing' },
    { key: 'professionalismScore', label: 'Professionalism' },
  ],
  Demo: [
    { key: 'openingScore', label: 'Opening & Rapport' },
    { key: 'discoveryScore', label: 'Discovery / Needs' },
    { key: 'valuePropScore', label: 'Product Presentation' },
    { key: 'callControlScore', label: 'Feature Coverage' },
    { key: 'objectionHandlingScore', label: 'Objection Handling' },
    { key: 'closingScore', label: 'Closing & Next Steps' },
  ],
};
const SCORE_FIELDS = SCORE_FIELDS_BY_TYPE.Call;

const OUTCOME_META = {
  // Call outcomes
  'Demo Scheduled':      { color: '#059669', bg: '#ecfdf5', icon: '●' },
  'Demo Confirmation':   { color: '#0d9488', bg: '#f0fdfa', icon: '●' },
  'Demo Reschedule':     { color: '#9333ea', bg: '#faf5ff', icon: '●' },
  'Interested':          { color: '#10b981', bg: '#ecfdf5', icon: '●' },
  'Callback Scheduled':  { color: '#2563eb', bg: '#eff6ff', icon: '●' },
  'Bad Timing':          { color: '#f59e0b', bg: '#fffbeb', icon: '●' },
  'Gatekeeper Block':    { color: '#d97706', bg: '#fffbeb', icon: '●' },
  'Not Interested':      { color: '#dc2626', bg: '#fef2f2', icon: '●' },
  'Already Has Solution':{ color: '#b91c1c', bg: '#fef2f2', icon: '●' },
  'No Answer/Voicemail': { color: '#6b7280', bg: '#f3f4f6', icon: '●' },
  'Wrong Number':        { color: '#9ca3af', bg: '#f9fafb', icon: '●' },
  // Nesting-call outcomes (rep gathered intel without pitching a demo)
  'Info Gathered':       { color: '#0369a1', bg: '#f0f9ff', icon: '●' },
  'Partial Info':        { color: '#0891b2', bg: '#ecfeff', icon: '●' },
  // Demo outcomes
  'Demo Completed':        { color: '#047857', bg: '#ecfdf5', icon: '●' },
  'Demo - Needs Follow-up':{ color: '#0891b2', bg: '#ecfeff', icon: '●' },
  'Discovery Call':        { color: '#2563eb', bg: '#eff6ff', icon: '●' },
  'Onboarding Meeting':    { color: '#7c3aed', bg: '#f5f3ff', icon: '●' },
  'Support / Training':    { color: '#0ea5e9', bg: '#f0f9ff', icon: '●' },
  'Contract Discussion':   { color: '#65a30d', bg: '#f7fee7', icon: '●' },
  'Partnership Discussion':{ color: '#9333ea', bg: '#faf5ff', icon: '●' },
  'No Show':               { color: '#dc2626', bg: '#fef2f2', icon: '●' },
  'Internal Meeting':      { color: '#9ca3af', bg: '#f9fafb', icon: '●' },
  'Other':                 { color: '#6b7280', bg: '#f3f4f6', icon: '●' },
};

// Outcomes that count as "a demo happened" — used for the Demos tab KPIs.
const DEMO_HAPPENED_OUTCOMES = new Set(['Demo Completed', 'Demo - Needs Follow-up']);
// Outcomes that shouldn't count as a real sales demo (existing customer or BD).
const DEMO_NON_SALES_OUTCOMES = new Set([
  'Onboarding Meeting', 'Support / Training', 'Partnership Discussion', 'Internal Meeting',
]);
// Used only on the Calls tab: distinct companies that got a fresh Demo Scheduled.
const DEMO_BOOKED_OUTCOMES = new Set(['Demo Scheduled']);
// Nesting-call outcomes (rep extracted intel without pitching a demo).
// Counted for the dedicated KPI card and the per-rep "nested" pill.
const NESTING_OUTCOMES = new Set(['Info Gathered', 'Partial Info']);

// Visual styling for the Setting/Nesting column on each call row.
const CALL_TYPE_META = {
  Setting: { color: '#1E6434', bg: '#ecfdf5' },
  Nesting: { color: '#0369a1', bg: '#f0f9ff' },
};

function resolveCallType(call) {
  if (call.callType === 'Setting' || call.callType === 'Nesting') {
    return call.callType;
  }
  return NESTING_OUTCOMES.has(call.outcome) ? 'Nesting' : 'Setting';
}

function toLocalDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtDur(s) {
  if (!s) return '—';
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function computeGlobalStats(data) {
  if (!data) return null;
  const allCalls = Object.values(data).flat();
  if (allCalls.length === 0) return null;

  let totalScore = 0, scoredCount = 0, totalDur = 0;
  // Call-tab counters
  let demosScheduled = 0, demoConfirmations = 0, demoReschedules = 0;
  let interested = 0, callbacks = 0, followUps = 0;
  // Nesting-outcome counters (Calls tab only)
  let infoGathered = 0, partialInfo = 0;
  // Demo-tab counters
  let demosHappened = 0, demosCompleted = 0, demosNeedsFollowUp = 0;
  let noShows = 0, nonSalesDemos = 0;
  const outcomes = {};
  const repCounts = {};
  const demoBookedCompanies = new Set();
  const demosHappenedCompanies = new Set();
  const callTypes = {
    Setting: { total: 0, outcomes: {} },
    Nesting: { total: 0, outcomes: {} },
  };
  const callTypeDemoBookedCompanies = {
    Setting: new Set(),
    Nesting: new Set(),
  };

  for (const c of allCalls) {
    if (c.overallScore != null) { totalScore += c.overallScore; scoredCount++; }
    totalDur += c.durationSeconds || 0;
    const o = c.outcome || 'Unknown';
    outcomes[o] = (outcomes[o] || 0) + 1;
    const resolvedCallType = resolveCallType(c);
    if (callTypes[resolvedCallType]) {
      callTypes[resolvedCallType].total++;
      callTypes[resolvedCallType].outcomes[o] = (callTypes[resolvedCallType].outcomes[o] || 0) + 1;
      if (DEMO_BOOKED_OUTCOMES.has(o)) {
        callTypeDemoBookedCompanies[resolvedCallType].add(c.companyId || c.companyName || `__row_${c.recordId}`);
      }
    }

    // Call-tab bucketing
    if (o === 'Demo Scheduled') demosScheduled++;
    if (o === 'Demo Confirmation') demoConfirmations++;
    if (o === 'Demo Reschedule') demoReschedules++;
    if (o === 'Interested') interested++;
    if (o === 'Callback Scheduled') callbacks++;
    if (c.followUpRequired) followUps++;
    if (DEMO_BOOKED_OUTCOMES.has(o)) {
      demoBookedCompanies.add(c.companyId || c.companyName || `__row_${c.recordId}`);
    }

    // Nesting-outcome bucketing
    if (o === 'Info Gathered') infoGathered++;
    if (o === 'Partial Info') partialInfo++;

    // Demo-tab bucketing
    if (o === 'Demo Completed') demosCompleted++;
    if (o === 'Demo - Needs Follow-up') demosNeedsFollowUp++;
    if (DEMO_HAPPENED_OUTCOMES.has(o)) {
      demosHappened++;
      demosHappenedCompanies.add(c.companyId || c.companyName || `__row_${c.recordId}`);
    }
    if (o === 'No Show') noShows++;
    if (DEMO_NON_SALES_OUTCOMES.has(o)) nonSalesDemos++;

    const rep = c.salesRep || 'Unknown';
    repCounts[rep] = (repCounts[rep] || 0) + 1;
  }

  const demosBooked = demoBookedCompanies.size;
  const distinctDemoCompanies = demosHappenedCompanies.size;
  callTypes.Setting.demosBooked = callTypeDemoBookedCompanies.Setting.size;
  callTypes.Nesting.demosBooked = callTypeDemoBookedCompanies.Nesting.size;

  return {
    totalCalls: allCalls.length,
    avgScore: scoredCount > 0 ? Math.round(totalScore / scoredCount) : null,
    avgDuration: allCalls.length > 0 ? Math.round(totalDur / allCalls.length) : 0,
    totalDuration: totalDur,
    // Call-tab fields
    demosScheduled,
    demoConfirmations,
    demoReschedules,
    demosBooked,
    interested,
    callbacks,
    followUps,
    // Nesting-outcome fields (Calls tab)
    infoGathered,
    partialInfo,
    // Demo-tab fields
    demosHappened,
    demosCompleted,
    demosNeedsFollowUp,
    distinctDemoCompanies,
    noShows,
    nonSalesDemos,
    // Shared
    outcomes,
    callTypes,
    reps: Object.keys(data).length,
    positiveRate: allCalls.length > 0 ? Math.round(((demosBooked + interested + callbacks) / allCalls.length) * 100) : 0,
  };
}

function ScoreBar({ value, max = 10 }) {
  if (value == null) return <span className="st-muted">—</span>;
  const pct = Math.min((value / max) * 100, 100);
  const tier = pct >= 70 ? 'high' : pct >= 40 ? 'mid' : 'low';
  return (
    <div className="st-score-bar">
      <div className="st-score-bar-track">
        <div className={`st-score-bar-fill st-score-${tier}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="st-score-bar-val">{value}</span>
    </div>
  );
}

function OutcomeBadge({ outcome }) {
  const meta = OUTCOME_META[outcome] || { color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span className="st-badge" style={{ color: meta.color, background: meta.bg }}>
      {outcome || 'Unknown'}
    </span>
  );
}

function CallTypeBadge({ callType }) {
  if (!callType) return <span className="st-muted">—</span>;
  const meta = CALL_TYPE_META[callType];
  if (!meta) {
    // Unknown / future values — render literally with a neutral style so we
    // don't silently drop them.
    return (
      <span className="st-badge" style={{ color: '#6b7280', background: '#f3f4f6' }}>
        {callType}
      </span>
    );
  }
  const title = callType === 'Setting'
    ? 'Setting call: rep was trying to book a demo'
    : 'Nesting call: rep was gathering intel without pitching a demo';
  return (
    <span
      className="st-badge"
      style={{ color: meta.color, background: meta.bg }}
      title={title}
    >
      {callType}
    </span>
  );
}

function KpiBanner({ stats, type }) {
  return type === 'Demo'
    ? <DemoKpiBanner stats={stats} />
    : <CallKpiBanner stats={stats} />;
}

function CallKpiBanner({ stats }) {
  const [showOthers, setShowOthers] = useState(false);

  // Outcomes that have their own dedicated tile and so should NOT be
  // double-counted inside the "Other Outcomes" drawer:
  //   - Demo Scheduled            → "Demos Booked" hero card
  //   - Info Gathered/Partial Info   → nesting row
  const EXCLUDED_FROM_OTHER = new Set([
    'Demo Scheduled',
    'Info Gathered', 'Partial Info',
  ]);
  const settingStats = stats.callTypes?.Setting || { total: 0, outcomes: {} };
  const nestingStats = stats.callTypes?.Nesting || { total: 0, outcomes: {} };
  const getNestingOutcome = (outcome) => nestingStats.outcomes[outcome] || 0;

  const demosBooked = settingStats.demosBooked || 0;
  const nonDemoOutcomes = [];
  for (const [outcome, count] of Object.entries(settingStats.outcomes)) {
    if (!EXCLUDED_FROM_OTHER.has(outcome)) {
      nonDemoOutcomes.push([outcome, count]);
    }
  }
  nonDemoOutcomes.sort((a, b) => b[1] - a[1]);
  const nonDemoTotal = nonDemoOutcomes.reduce((sum, [, c]) => sum + c, 0);

  const infoGathered = getNestingOutcome('Info Gathered');
  const partialInfo = getNestingOutcome('Partial Info');
  const pct = (count, total = stats.totalCalls) => (total > 0 ? Math.round((count / total) * 100) : 0);

  return (
    <div className="st-banner">
      <div className="st-call-kpi-total-row">
        <div className="st-kpi-card st-kpi-hero st-call-kpi-total-card">
          <div className="st-kpi-label">Total Calls</div>
          <div className="st-kpi-value">{stats.totalCalls}</div>
          <div className="st-kpi-sub">{stats.reps} reps</div>
        </div>
      </div>

      <div className="st-call-kpi-row">
        <div className="st-call-kpi-row-heading">
          <div className="st-kpi-label">Calls to Set</div>
          <div className="st-kpi-value">{settingStats.total}</div>
          <div className="st-kpi-sub">{pct(settingStats.total)}% of calls</div>
        </div>
        <div className="st-call-kpi-outcomes">
          <div className="st-kpi-card st-repeat-card st-call-kpi-outcome-card" style={{ borderLeft: `3px solid ${OUTCOME_META['Demo Scheduled'].color}` }}>
            <div className="st-kpi-label">Demos Booked</div>
            <div className="st-kpi-value st-accent-green">{demosBooked}</div>
            <div className="st-kpi-sub">distinct companies · {pct(demosBooked, settingStats.total)}%</div>
          </div>
          <div
            className={`st-kpi-card st-repeat-card st-call-kpi-outcome-card st-kpi-other ${showOthers ? 'st-kpi-other-open' : ''}`}
            onClick={() => setShowOthers(prev => !prev)}
            style={{ cursor: 'pointer' }}
          >
            <div className="st-kpi-label">Other Outcomes</div>
            <div className="st-kpi-value st-muted-value">{nonDemoTotal}</div>
            <div className="st-kpi-sub">
              click to {showOthers ? 'collapse' : 'expand'}
              <span className={`st-chevron-inline ${showOthers ? 'st-chevron-open' : ''}`}>&#9662;</span>
            </div>
          </div>
        </div>
      </div>

      <div className="st-call-kpi-row">
        <div className="st-call-kpi-row-heading">
          <div className="st-kpi-label">Calls to Nest</div>
          <div className="st-kpi-value">{nestingStats.total}</div>
          <div className="st-kpi-sub">{pct(nestingStats.total)}% of calls</div>
        </div>
        <div className="st-call-kpi-outcomes st-call-kpi-outcomes-nesting">
          <div className="st-kpi-card st-repeat-card st-call-kpi-outcome-card" style={{ borderLeft: `3px solid ${OUTCOME_META['Info Gathered'].color}` }}>
            <div className="st-kpi-label">Info Gathered</div>
            <div className="st-kpi-value" style={{ color: OUTCOME_META['Info Gathered'].color }}>{infoGathered}</div>
            <div className="st-kpi-sub">{pct(infoGathered, nestingStats.total)}% of nesting calls</div>
          </div>
          <div className="st-kpi-card st-repeat-card st-call-kpi-outcome-card" style={{ borderLeft: `3px solid ${OUTCOME_META['Partial Info'].color}` }}>
            <div className="st-kpi-label">Partial Info</div>
            <div className="st-kpi-value" style={{ color: OUTCOME_META['Partial Info'].color }}>{partialInfo}</div>
            <div className="st-kpi-sub">{pct(partialInfo, nestingStats.total)}% of nesting calls</div>
          </div>
        </div>
      </div>

      {showOthers && (
        <div className="st-banner-others">
          {nonDemoOutcomes.map(([outcome, count]) => {
            const meta = OUTCOME_META[outcome] || { color: '#6b7280' };
            const pct = settingStats.total > 0 ? Math.round((count / settingStats.total) * 100) : 0;
            return (
              <div key={outcome} className="st-kpi-card st-outcome-card" style={{ borderLeft: `3px solid ${meta.color}` }}>
                <div className="st-kpi-label">{outcome}</div>
                <div className="st-kpi-value" style={{ color: meta.color }}>{count}</div>
                <div className="st-kpi-sub">{pct}%</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DemoKpiBanner({ stats }) {
  const [showOthers, setShowOthers] = useState(false);

  const totalDemos = stats.totalCalls;   // "totalCalls" is really total-touchpoints-in-view
  const demosHappened = stats.demosHappened || 0;        // Completed + Needs Follow-up
  const demosCompleted = stats.demosCompleted || 0;      // pure success
  const demosNeedsFollowUp = stats.demosNeedsFollowUp || 0;
  const noShows = stats.noShows || 0;
  const nonSales = stats.nonSalesDemos || 0;             // Onboarding + Support + Partnership + Internal

  // "Other" drawer is every outcome that isn't one of the headline buckets.
  const EXCLUDED_FROM_OTHER = new Set([
    'Demo Completed', 'Demo - Needs Follow-up', 'No Show',
    'Onboarding Meeting', 'Support / Training', 'Partnership Discussion', 'Internal Meeting',
  ]);
  const otherOutcomes = [];
  for (const [outcome, count] of Object.entries(stats.outcomes)) {
    if (!EXCLUDED_FROM_OTHER.has(outcome)) {
      otherOutcomes.push([outcome, count]);
    }
  }
  otherOutcomes.sort((a, b) => b[1] - a[1]);
  const otherTotal = otherOutcomes.reduce((sum, [, c]) => sum + c, 0);

  const pct = (n) => (totalDemos > 0 ? Math.round((n / totalDemos) * 100) : 0);

  return (
    <div className="st-banner">
      <div className="st-banner-top">
        <div className="st-kpi-card st-kpi-hero">
          <div className="st-kpi-label">Total Demos</div>
          <div className="st-kpi-value">{totalDemos}</div>
          <div className="st-kpi-sub">{stats.reps} reps · {stats.distinctDemoCompanies || 0} companies</div>
        </div>
        <div className="st-kpi-card st-kpi-hero st-kpi-demo">
          <div className="st-kpi-label">Demos That Happened</div>
          <div className="st-kpi-value st-accent-green">{demosHappened}</div>
          <div className="st-kpi-sub">Completed + Needs Follow-up · {pct(demosHappened)}%</div>
        </div>
        <div
          className="st-kpi-card st-kpi-hero"
          style={{ borderLeft: `3px solid ${OUTCOME_META['No Show'].color}` }}
          title="Prospect scheduled but didn't attend"
        >
          <div className="st-kpi-label">No Shows</div>
          <div className="st-kpi-value" style={{ color: OUTCOME_META['No Show'].color }}>{noShows}</div>
          <div className="st-kpi-sub">{pct(noShows)}% of demos</div>
        </div>
      </div>

      {(demosCompleted > 0 || demosNeedsFollowUp > 0 || nonSales > 0) && (
        <div className="st-banner-repeats">
          <div
            className="st-kpi-card st-repeat-card"
            style={{ borderLeft: `3px solid ${OUTCOME_META['Demo Completed'].color}` }}
            title="Demos where the prospect was clearly engaged / ready to close"
          >
            <div className="st-kpi-label">Demo Completed</div>
            <div className="st-kpi-value" style={{ color: OUTCOME_META['Demo Completed'].color }}>{demosCompleted}</div>
            <div className="st-kpi-sub">strong engagement · {pct(demosCompleted)}%</div>
          </div>
          <div
            className="st-kpi-card st-repeat-card"
            style={{ borderLeft: `3px solid ${OUTCOME_META['Demo - Needs Follow-up'].color}` }}
            title="Demo happened, prospect is non-committal, needs follow-up"
          >
            <div className="st-kpi-label">Needs Follow-up</div>
            <div className="st-kpi-value" style={{ color: OUTCOME_META['Demo - Needs Follow-up'].color }}>{demosNeedsFollowUp}</div>
            <div className="st-kpi-sub">prospect non-committal · {pct(demosNeedsFollowUp)}%</div>
          </div>
          {nonSales > 0 && (
            <div
              className="st-kpi-card st-repeat-card"
              style={{ borderLeft: `3px solid ${OUTCOME_META['Onboarding Meeting'].color}` }}
              title="Onboarding / Support / Training / Partnership / Internal — not a sales demo"
            >
              <div className="st-kpi-label">Non-Sales Meetings</div>
              <div className="st-kpi-value" style={{ color: OUTCOME_META['Onboarding Meeting'].color }}>{nonSales}</div>
              <div className="st-kpi-sub">onboarding, support, partnership, internal</div>
            </div>
          )}
        </div>
      )}

      {otherTotal > 0 && (
        <div style={{ marginTop: 12 }}>
          <div
            className={`st-kpi-card st-kpi-other ${showOthers ? 'st-kpi-other-open' : ''}`}
            onClick={() => setShowOthers(prev => !prev)}
            style={{ cursor: 'pointer', padding: '10px 14px' }}
          >
            <div className="st-kpi-label" style={{ display: 'inline-block' }}>Other Outcomes</div>
            <span className="st-muted-value" style={{ marginLeft: 8, fontWeight: 600 }}>{otherTotal}</span>
            <span className="st-kpi-sub" style={{ marginLeft: 12 }}>
              click to {showOthers ? 'collapse' : 'expand'}
              <span className={`st-chevron-inline ${showOthers ? 'st-chevron-open' : ''}`}>&#9662;</span>
            </span>
          </div>
          {showOthers && (
            <div className="st-banner-others" style={{ marginTop: 8 }}>
              {otherOutcomes.map(([outcome, count]) => {
                const meta = OUTCOME_META[outcome] || { color: '#6b7280' };
                return (
                  <div key={outcome} className="st-kpi-card st-outcome-card" style={{ borderLeft: `3px solid ${meta.color}` }}>
                    <div className="st-kpi-label">{outcome}</div>
                    <div className="st-kpi-value" style={{ color: meta.color }}>{count}</div>
                    <div className="st-kpi-sub">{pct(count)}%</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RepSection({ rep, calls, expandedRows, toggleRow, isCollapsed, toggleCollapse, type }) {
  // Label the "highlight pill" after the count differently per tab.
  // Call tab: highlight fresh demo bookings.
  // Demo tab: highlight demos that actually happened (Completed + Needs Follow-up).
  const HIGHLIGHT_OUTCOMES = type === 'Demo'
    ? DEMO_HAPPENED_OUTCOMES
    : new Set(['Demo Scheduled']);
  const rowNoun = type === 'Demo' ? 'demo' : 'call';
  const highlightNoun = type === 'Demo' ? 'happened' : 'demo';

  const outcomes = {};
  let highlightCount = 0;
  // Per-rep nested count = Info Gathered + Partial Info. Calls tab only,
  // since these outcomes don't apply on Demo touchpoints.
  let nestedCount = 0;
  for (const c of calls) {
    const o = c.outcome || 'Unknown';
    outcomes[o] = (outcomes[o] || 0) + 1;
    if (HIGHLIGHT_OUTCOMES.has(o)) highlightCount++;
    if (type === 'Call' && NESTING_OUTCOMES.has(o)) nestedCount++;
  }

  return (
    <div className={`st-rep-section ${isCollapsed ? 'st-rep-collapsed' : ''}`}>
      <div className="st-rep-header" onClick={toggleCollapse}>
        <div className="st-rep-header-left">
          <span className={`st-rep-expand-icon ${isCollapsed ? '' : 'st-rep-expand-open'}`}>&#9656;</span>
          <span className="st-rep-name">{rep}</span>
          <span className="st-rep-pill">{calls.length} {rowNoun}{calls.length !== 1 ? 's' : ''}</span>
          {highlightCount > 0 && (
            <span className="st-rep-pill st-accent-green">
              {highlightCount} {highlightNoun}{highlightCount !== 1 ? 's' : ''}
            </span>
          )}
          {nestedCount > 0 && (
            <span
              className="st-rep-pill st-accent-blue"
              title="Nesting calls — Info Gathered + Partial Info"
            >
              {nestedCount} nested
            </span>
          )}
        </div>
      </div>

      {!isCollapsed && (
        <div className="st-rep-body">
          <div className="st-rep-mini-stats">
            {Object.entries(outcomes).sort((a, b) => b[1] - a[1]).map(([o, count]) => {
              const meta = OUTCOME_META[o] || { color: '#6b7280' };
              return (
                <span key={o} className="st-mini-stat" style={{ color: meta.color }}>
                  <strong>{count}</strong> {o}
                </span>
              );
            })}
          </div>

          <table className="st-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Company</th>
                <th>Outcome</th>
                {/* Call Type only applies to Aircall touchpoints. */}
                {type === 'Call' && <th>Type</th>}
                <th>Duration</th>
                <th>Score</th>
                <th>{type === 'Demo' ? 'Attendees' : 'Dir'}</th>
                <th style={{ width: 28 }}></th>
              </tr>
            </thead>
            <tbody>
              {calls.map(call => (
                <CallRow
                  key={call.recordId}
                  call={call}
                  type={type}
                  isExpanded={!!expandedRows[call.recordId]}
                  onToggle={() => toggleRow(call.recordId)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CallRow({ call, type, isExpanded, onToggle }) {
  const scoreTier = call.overallScore >= 40 ? 'st-accent-green' : call.overallScore >= 25 ? 'st-accent-amber' : 'st-accent-red';

  return (
    <>
      <tr className={`st-row ${isExpanded ? 'st-row-expanded' : ''}`} onClick={onToggle}>
        <td>
          <div className="st-cell-date">{fmtDate(call.callDate)}</div>
          <div className="st-cell-time">{fmtTime(call.callDate)}</div>
        </td>
        <td className="st-cell-company">{call.companyName}</td>
        <td><OutcomeBadge outcome={call.outcome} /></td>
        {type === 'Call' && (
          <td><CallTypeBadge callType={call.callType} /></td>
        )}
        <td>{fmtDur(call.durationSeconds)}</td>
        <td>
          {call.overallScore != null
            ? <span className={`st-score-inline ${scoreTier}`}>{call.overallScore}</span>
            : <span className="st-muted">—</span>}
        </td>
        <td className="st-muted">{call.direction || '—'}</td>
        <td><span className={`st-chevron-sm ${isExpanded ? 'st-chevron-open' : ''}`}>&#9662;</span></td>
      </tr>
      {isExpanded && (
        <tr className="st-detail-row">
          {/* Calls tab adds an extra "Type" column, so the detail row needs
              to span one extra cell to stay full-width. */}
          <td colSpan={type === 'Call' ? 8 : 7}>
            <div className="st-detail-grid">
              <div className="st-detail-left">
                <div className="st-detail-section-title">Score Breakdown</div>
                <div className="st-score-grid">
                  {(SCORE_FIELDS_BY_TYPE[type] || SCORE_FIELDS).map(f => (
                    <div key={f.key} className="st-score-item">
                      <span className="st-score-label">{f.label}</span>
                      <ScoreBar value={call[f.key]} />
                    </div>
                  ))}
                </div>

                <div className="st-detail-meta">
                  {call.dealLikelihood && <div><span className="st-meta-label">Deal Likelihood</span> {call.dealLikelihood}</div>}
                  {call.practiceSize && <div><span className="st-meta-label">Practice Size</span> {call.practiceSize}</div>}
                  {call.pmsSystem && <div><span className="st-meta-label">PMS</span> {call.pmsSystem}</div>}
                  {call.currentPhoneSystem && <div><span className="st-meta-label">Phone System</span> {call.currentPhoneSystem}</div>}
                </div>
              </div>

              <div className="st-detail-right">
                {call.summary && (
                  <div className="st-detail-block">
                    <div className="st-detail-section-title">Summary</div>
                    <p>{call.summary}</p>
                  </div>
                )}
                {call.strengths && (
                  <div className="st-detail-block">
                    <div className="st-detail-section-title">Strengths</div>
                    <p>{call.strengths}</p>
                  </div>
                )}
                {call.improvements && (
                  <div className="st-detail-block">
                    <div className="st-detail-section-title">Areas to Improve</div>
                    <p>{call.improvements}</p>
                  </div>
                )}
                {call.coachingFeedback && (
                  <div className="st-detail-block">
                    <div className="st-detail-section-title">Coaching</div>
                    <p>{call.coachingFeedback}</p>
                  </div>
                )}
              </div>
            </div>

            {call.followUpRequired && (
              <div className="st-followup-banner">
                <strong>Follow-up Required</strong>
                {call.followUpNotes && <span> — {call.followUpNotes}</span>}
              </div>
            )}

            {call.recordingLink && (
              <a href={call.recordingLink} target="_blank" rel="noopener noreferrer" className="st-recording-link">
                Listen to Recording &rarr;
              </a>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

const TYPE_TABS = [
  { key: 'Call', label: 'Calls' },
  { key: 'Demo', label: 'Demos' },
];

export default function SalesTouchpoints({ type: typeProp } = {}) {
  const today = new Date();
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 6);

  const [type, setType] = useState(typeProp || 'Call');
  const [startDate, setStartDate] = useState(toLocalDate(weekAgo));
  const [endDate, setEndDate] = useState(toLocalDate(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [collapsedReps, setCollapsedReps] = useState(null);
  const [activePreset, setActivePreset] = useState('week');

  const abortCtrlRef = useRef(null);
  const requestSeqRef = useRef(0);

  const stats = useMemo(() => {
    const s = computeGlobalStats(data);
    console.log('[SalesTouchpoints] stats recomputed', {
      hasData: !!data,
      dataKeys: data ? Object.keys(data).length : 0,
      totalCalls: s?.totalCalls ?? 0,
      reps: s?.reps ?? 0,
      outcomes: s?.outcomes,
    });
    return s;
  }, [data]);

  const fetchData = useCallback(async () => {
    const fetchId = Math.random().toString(36).slice(2, 8);

    if (abortCtrlRef.current) {
      console.log(`[SalesTouchpoints][${fetchId}] aborting previous in-flight request`);
      abortCtrlRef.current.abort();
    }
    const ctrl = new AbortController();
    abortCtrlRef.current = ctrl;
    const mySeq = ++requestSeqRef.current;

    setLoading(true);
    setError('');
    try {
      const start = new Date(startDate + 'T00:00:00').toISOString();
      const end = new Date(endDate + 'T23:59:59').toISOString();
      const params = new URLSearchParams({ startDate: start, endDate: end });
      if (type) params.set('type', type);
      const url = `/api/sales-touchpoints?${params.toString()}`;
      console.log(`[SalesTouchpoints][${fetchId}] fetchData START (seq=${mySeq})`, {
        type,
        startDate,
        endDate,
        startISO: start,
        endISO: end,
        tzOffsetMin: new Date().getTimezoneOffset(),
        url,
      });
      const t0 = performance.now();
      const result = await fetchFromDentalAPI(url, { signal: ctrl.signal });
      const ms = Math.round(performance.now() - t0);

      if (mySeq !== requestSeqRef.current) {
        console.warn(`[SalesTouchpoints][${fetchId}] STALE response ignored (seq=${mySeq}, current=${requestSeqRef.current}, ${ms}ms)`);
        return;
      }

      if (result.success) {
        const reps = Object.keys(result.data || {});
        const totalFromData = reps.reduce((sum, r) => sum + (result.data[r]?.length || 0), 0);
        console.log(`[SalesTouchpoints][${fetchId}] fetchData OK (${ms}ms, seq=${mySeq})`, {
          serverTotalCalls: result.totalCalls,
          totalFromData,
          repCount: reps.length,
          repBreakdown: reps.map(r => ({ rep: r, calls: result.data[r]?.length || 0 })),
          mismatch: result.totalCalls !== totalFromData ? 'serverTotalCalls != sum(reps)' : null,
        });
        if (Array.isArray(result.data)) {
          console.warn(`[SalesTouchpoints][${fetchId}] result.data is an ARRAY, expected object keyed by rep`);
        }
        setData(result.data);
        setCollapsedReps(prev => {
          if (prev !== null) return prev;
          const collapsed = {};
          for (const rep of reps) collapsed[rep] = true;
          return collapsed;
        });
      } else {
        console.warn(`[SalesTouchpoints][${fetchId}] fetchData FAILED (${ms}ms)`, result);
        setError(result.error || 'Failed to fetch data');
      }
    } catch (err) {
      if (err?.name === 'AbortError') {
        console.log(`[SalesTouchpoints][${fetchId}] fetchData aborted (seq=${mySeq})`);
        return;
      }
      if (mySeq !== requestSeqRef.current) {
        console.warn(`[SalesTouchpoints][${fetchId}] stale error ignored (seq=${mySeq})`, err);
        return;
      }
      console.error(`[SalesTouchpoints][${fetchId}] fetchData THREW`, err);
      setError(err.message);
    } finally {
      if (mySeq === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [startDate, endDate, type]);

  useEffect(() => {
    console.log('[SalesTouchpoints] useEffect -> fetchData (deps changed)', {
      type,
      startDate,
      endDate,
      activePreset,
    });
    fetchData();
  }, [fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRow = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleRep = (rep) => setCollapsedReps(prev => ({ ...(prev || {}), [rep]: !(prev || {})[rep] }));

  const setPreset = (key) => {
    setActivePreset(key);
    const now = new Date();
    const todayStr = toLocalDate(now);
    let newStart = startDate;
    let newEnd = endDate;
    if (key === 'today') {
      newStart = todayStr;
      newEnd = todayStr;
    } else if (key === 'yesterday') {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      const yStr = toLocalDate(y);
      newStart = yStr;
      newEnd = yStr;
    } else if (key === 'week') {
      const w = new Date(now);
      w.setDate(w.getDate() - 6);
      newStart = toLocalDate(w);
      newEnd = todayStr;
    } else if (key === 'month') {
      const m = new Date(now);
      m.setDate(m.getDate() - 29);
      newStart = toLocalDate(m);
      newEnd = todayStr;
    }
    console.log('[SalesTouchpoints] setPreset', {
      key,
      prevStartDate: startDate,
      prevEndDate: endDate,
      newStartDate: newStart,
      newEndDate: newEnd,
      startChanged: newStart !== startDate,
      endChanged: newEnd !== endDate,
      willRefetch: newStart !== startDate || newEnd !== endDate,
    });
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  console.log('[SalesTouchpoints] render', {
    startDate,
    endDate,
    activePreset,
    loading,
    hasData: !!data,
    statsTotalCalls: stats?.totalCalls ?? null,
    error: error || null,
  });

  return (
    <div className="st-container">
      {/* Type sub-tabs */}
      <div className="st-type-tabs">
        {TYPE_TABS.map(t => (
          <button
            key={t.key}
            className={`st-type-tab ${type === t.key ? 'st-type-tab-active' : ''}`}
            onClick={() => {
              if (type === t.key) return;
              console.log('[SalesTouchpoints] type tab changed', { from: type, to: t.key });
              setType(t.key);
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="st-controls">
        <div className="st-controls-left">
          <div className="st-presets">
            {[['today', 'Today'], ['yesterday', 'Yesterday'], ['week', '7 Days'], ['month', '30 Days']].map(([key, label]) => (
              <button
                key={key}
                className={`st-preset-btn ${activePreset === key ? 'st-preset-active' : ''}`}
                onClick={() => setPreset(key)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="st-date-range">
            <input
              type="date"
              value={startDate}
              onChange={e => {
                console.log('[SalesTouchpoints] startDate input changed', { from: startDate, to: e.target.value });
                setStartDate(e.target.value);
                setActivePreset('');
              }}
            />
            <span className="st-date-sep">&ndash;</span>
            <input
              type="date"
              value={endDate}
              onChange={e => {
                console.log('[SalesTouchpoints] endDate input changed', { from: endDate, to: e.target.value });
                setEndDate(e.target.value);
                setActivePreset('');
              }}
            />
          </div>
        </div>
        <button
          className="st-refresh-btn"
          onClick={() => {
            console.log('[SalesTouchpoints] Refresh button clicked', { startDate, endDate, activePreset });
            fetchData();
          }}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="st-error">{error}</div>}

      {loading && <div className="st-loading">Loading touchpoints…</div>}

      {/* KPI Banner */}
      {!loading && stats && (
        <KpiBanner stats={stats} type={type} />
      )}

      {!loading && data && Object.keys(data).length === 0 && (
        <div className="st-empty">
          No {type === 'Demo' ? 'demos' : 'calls'} found for this date range.
        </div>
      )}

      {/* Rep Sections */}
      {!loading && data && Object.entries(data)
        .sort((a, b) => b[1].length - a[1].length)
        .map(([rep, calls]) => (
          <RepSection
            key={rep}
            type={type}
            rep={rep}
            calls={calls}
            expandedRows={expandedRows}
            toggleRow={toggleRow}
            isCollapsed={collapsedReps === null || !!collapsedReps[rep]}
            toggleCollapse={() => toggleRep(rep)}
          />
        ))}
    </div>
  );
}
